/**
 * Responsibility: one row per catalogue - how much is read, and how stale.
 *
 * **Coverage and freshness are two columns because they are two failures.**
 * A catalogue can be fully read and a month out of date, or freshly read and
 * a tenth crawled, and a single percentage would call both of those "87%".
 * So sets-read answers "has it been reached" and sets-due answers "is it
 * keeping up", and they sit side by side.
 *
 * **Due is what the crawler owes.** Not the sets it has never read - the
 * sets it has never read *plus* the ones whose reading is older than this
 * catalogue's own interval. A daily catalogue with every set read last month
 * owes all of them, and a page counting only never-read sets would show it
 * at 100%.
 *
 * Sorted daily-first, then by what is owed. The daily catalogues are the
 * ones anybody is actually screening, so a backlog there matters in a way
 * that a backlog in boxing does not.
 *
 * **Where it lives is two symbols**, the same pair the Data Hub uses. The
 * cloud one is read from the receipt the backup run leaves behind, so a
 * blue cloud means an upload that happened rather than a backup that is
 * configured - which is the only version of that claim worth showing.
 */
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { CataloguePace } from '../../api/client';
import { readableBytes, stamp } from './dataHubFormat';
import { StorageIcons } from './StorageIcons';

/** Sets read over sets that exist, or a dash where nobody has listed them. */
function coverageLabel(row: CataloguePace): string {
  if (row.sets_known === null) return '—';
  return `${(row.coverage * 100).toFixed(0)}%`;
}

function setsLabel(row: CataloguePace): string {
  if (row.sets_known === null) return `${row.sets_read} / ?`;
  return `${row.sets_read} / ${row.sets_known}`;
}

export function IndexCatalogueTable({ catalogues }: { catalogues: CataloguePace[] }) {
  const rows = [...catalogues].sort((left, right) => {
    if (left.daily !== right.daily) return left.daily ? -1 : 1;
    return right.sets_due - left.sets_due;
  });

  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell sx={{ pl: 0 }}>Catalogue</TableCell>
          <TableCell>Pace</TableCell>
          <TableCell align="right" sx={{ minWidth: 150 }}>
            Sets read
          </TableCell>
          <TableCell align="right">Owed</TableCell>
          <TableCell align="right">Cards</TableCell>
          <TableCell align="right">Size</TableCell>
          <TableCell>Where</TableCell>
          <TableCell align="right" sx={{ pr: 0 }}>
            Oldest reading
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.key}>
            <TableCell sx={{ pl: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {row.label}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {row.host.replace('www.', '')}
              </Typography>
            </TableCell>

            <TableCell>
              <Tooltip
                title={
                  row.daily
                    ? 'Refreshed every day, ahead of everything else that is due.'
                    : `Read every ${row.refresh_days} days, in whatever capacity the daily catalogues leave.`
                }
              >
                <Chip
                  size="small"
                  variant="outlined"
                  color={row.daily ? 'primary' : 'default'}
                  label={row.daily ? 'daily' : `${row.refresh_days}d`}
                />
              </Tooltip>
            </TableCell>

            <TableCell align="right">
              <Stack spacing={0.5} alignItems="flex-end">
                <Typography variant="body2">
                  {setsLabel(row)}
                  <Typography
                    component="span"
                    variant="caption"
                    color="text.secondary"
                    sx={{ ml: 0.75 }}
                  >
                    {coverageLabel(row)}
                  </Typography>
                </Typography>
                {row.sets_known === null ? (
                  <Tooltip title="The site's set list has not been fetched yet, so there is nothing to measure against. Not the same as the catalogue being empty.">
                    <Typography variant="caption" color="text.secondary">
                      not listed yet
                    </Typography>
                  </Tooltip>
                ) : (
                  <LinearProgress
                    variant="determinate"
                    value={Math.max(Math.min(row.coverage * 100, 100), 0.5)}
                    color={row.coverage >= 0.99 ? 'success' : 'primary'}
                    sx={{ height: 5, borderRadius: 1, width: '100%' }}
                  />
                )}
              </Stack>
            </TableCell>

            <TableCell align="right">
              <Tooltip title="Never read, or read longer ago than this catalogue's own interval.">
                <Typography
                  variant="body2"
                  color={row.sets_due === 0 ? 'text.disabled' : 'text.primary'}
                >
                  {row.sets_due}
                </Typography>
              </Tooltip>
            </TableCell>

            <TableCell align="right">
              {row.cards === null ? (
                <Typography variant="body2" color="text.disabled">
                  —
                </Typography>
              ) : (
                <>
                  <Typography variant="body2">{row.cards.toLocaleString()}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(row.cards_with_images ?? 0).toLocaleString()} with a picture
                  </Typography>
                </>
              )}
            </TableCell>

            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
              {row.cache_bytes > 0 ? (
                readableBytes(row.cache_bytes)
              ) : (
                <Typography variant="body2" color="text.disabled">
                  —
                </Typography>
              )}
            </TableCell>

            <TableCell>
              <StorageIcons
                onServer={row.on_disk}
                backedUp={row.backed_up}
                serverNote={
                  row.on_disk
                    ? 'The cache for this catalogue is on the box.'
                    : 'Nothing cached yet - this catalogue has not been crawled.'
                }
                cloudNote={
                  row.backed_up
                    ? 'Carried by the last nightly archive.'
                    : row.on_disk
                      ? 'Not in the last archive. It will ride the next nightly run.'
                      : 'Nothing to back up yet.'
                }
              />
            </TableCell>

            <TableCell align="right" sx={{ pr: 0, whiteSpace: 'nowrap' }}>
              <Typography variant="body2" color="text.secondary">
                {stamp(row.oldest_reading)}
              </Typography>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
